import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Avatar,
} from "@mui/material";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelectedBusiness } from "../../../redux/store";
import { useMutation } from "@apollo/client";
import { StyledDialog, StyledDialogTitle, StyledDialogContent, StyledDialogContentText, StyledDialogActions } from "../../../assets/css/styledComponent";
import { uploadToGCS } from "../../../util/upload/uploadToGCS";
import { DELETE_IMAGE, GENERATE_UPLOAD_URL, UPDATE_IMAGE } from "../../../graphql/mutations/image";
import { uploadToCloudinary } from "../../../util/upload/uploadToCloudinary"; 
import { BusinessQueryNames } from "../../../graphql/queries";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { useAuthStatus } from "../../../util/useAuthStatus";

const ImageEntryForm = ({ singleImageMode = true, avatar = false, staffId = null, isDisabled = false }) => {
  const [images, setImages] = useState([]);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const [openConfirmStyledDialog, setOpenConfirmStyledDialog] = useState(false);
  const selectedBusiness = useSelectedBusiness();
  const [generateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
  const [updateImage] = useMutation(UPDATE_IMAGE);
  const [deleteImage] = useMutation(DELETE_IMAGE);
  const { isLoggedIn } = useAuthStatus();

  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );

  useEffect(() => {
    let currentImages;
    if (avatar) {
      const currentStaffImage = selectedBusiness?.businessStaffs?.find(
        (staff) => Number(staff.id) === Number(staffId)
      )?.image

      currentImages = currentStaffImage
        ? [{ url: currentStaffImage?.url, name: "Profile Pic" }]
        : [];
    } else {
      currentImages = selectedBusiness?.businessBasic?.images
        ?.filter((item) => item.businessId !== null)
        .map((item) => ({
          id:item?.id,
          url: item?.url,
          name: `picture ${item.id}`,
        }));
    }
    setImages(currentImages);
  }, [selectedBusiness, staffId, avatar]);

  useEffect(() => {
    // Reload the business data if not available
    reloadBusinesses();
  }, [images, reloadBusinesses]);


  const handleAddImage = async (e) => {
    if (selectedBusiness) {
      try {
        const file = e.target.files?.[0]; 
        const url = process.env.REACT_APP_ENV === "development" ? await uploadToCloudinary(file) : await uploadToGCS(file, generateUploadUrl);

        // Save image to DB
        const id = staffId ? staffId : Number(selectedBusiness?.businessBasic?.id);

        await updateImage({
          variables: {
            id,
            url: `${avatar ? "staff" : "business"},${url}`,
          },
        });
        reloadBusinesses()
      } catch (error) {
        console.error("Upload error:", error);
      }
    }
    const files = e.target.files;
    if (files && files.length > 0) {
      const newImage = {
        url: URL.createObjectURL(files[0]),
        name: files[0].name.split(".").slice(0, -1).join("."),
      };

      if (singleImageMode) {
        setImages([newImage]); // Replace existing image 
      } else {
        const newImages = Array.from(files).map((file) => ({
          url: URL.createObjectURL(file),
          name: file.name.split(".").slice(0, -1).join("."),
        }));
        setImages([...images, ...newImages]); 
      }
      setIsChanged(true);
    }
  };

  const DraggableImage = ({ index }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "IMAGE",
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [showDelete, setShowDelete] = useState(false);

    const handleClick = () => {
      setShowDelete(!showDelete);
    };

    const handleDeleteImage = (index) => {
      setDeleteIndex(index);
      setOpenConfirmStyledDialog(true);
    };

    return (
      <div
        ref={drag}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: "move",
          position: "relative",
        }}
      >
        <Paper
          style={{ position: "relative" }}
          onClick={handleClick}
          onMouseEnter={() => setShowDelete(true)}
          onMouseLeave={() => setShowDelete(false)}
        >
          {avatar ? (
            <Avatar
              src={images[index].url}
              alt={`Image ${index}`}
              sx={{ width: 56, height: 56 }}
            />
          ) : (
            <img
              src={images[index].url}
              alt={`Image ${index}`}
              style={{ width: "100%" }}
            />
          )}
          <Typography variant="caption">{images[index].name}</Typography>
          {showDelete && (
            <Button onClick={() => handleDeleteImage(index)}>Delete</Button>
          )}
        </Paper>
      </div>
    );
  };

  const DropZone = ({ index, children }) => {
    const [, drop] = useDrop({
      accept: "IMAGE",
      drop: (item) => {
        const dragIndex = item.index;
        const dropIndex = index;

        if (dragIndex === dropIndex) return;
        const updatedImages = [...images];
        const [removed] = updatedImages.splice(dragIndex, 1);
        updatedImages.splice(dropIndex, 0, removed);
        setImages(updatedImages);
        setIsChanged(true);
      },
    });

    return <div ref={drop}>{children}</div>;
  };

  const handleDeleteImage = async () => {
    if (deleteIndex !== null) {
      // Assuming each image object in `images` has a URL like:
      // https://storage.googleapis.com/<bucketName>/uploads/1639824557653-feature-image-2.png
      // Extract the file path from the URL.
      const imageToDelete = images[deleteIndex];
      // The bucket URL prefix
      const bucketUrl = `https://storage.googleapis.com/${process.env.REACT_APP_GCLOUD_BUCKET_NAME}/`; 
      // Remove the bucket URL to get the relative file path
      const filename = imageToDelete.url.replace(bucketUrl, '');

      try {
        // Call the GraphQL mutation to delete the file.
        // The mutation should delete the file from Cloud Storage (and optionally from your local DB) and return true on success.
        const { data } = await deleteImage({ variables: { filename, id: imageToDelete.id } });

        if (data.deleteImage) {
      // Update local state: remove the image from the array.
          const updatedImages = images.filter((_, idx) => idx !== deleteIndex);
          setImages(updatedImages);
          setIsChanged(true);
          setOpenConfirmStyledDialog(false);
          setDeleteIndex(null); // Reset deleteIndex after deletion 
        } else {
          console.error("Deletion failed: Mutation returned false");
        }
      } catch (error) {
        console.error("Deletion error:", error);
      }
    }
  };

  const cancelDelete = () => {
    setOpenConfirmStyledDialog(false);
    setDeleteIndex(null); // Reset deleteIndex if canceling
  };

  const handleSave = () => {
    // Your save logic here
    setIsChanged(false);
  };

  return (
    <Container>
      <Box mt={4}>
        <Typography variant="h4" gutterBottom>
          {"Photo"}
        </Typography>
        <Box mt={2}>
          <input
            type="file"
            accept="image/*"
            onChange={handleAddImage}
            style={{ display: "none" }}
            id="upload-input"
          />
          <label htmlFor="upload-input">
            <Button disabled={isDisabled} variant="contained" component="span">
              {singleImageMode && images.length > 0
                ? "Update Image"
                : "Upload Image"}
            </Button>
          </label>
        </Box>
      </Box>
      <Box mt={4}>
        <DndProvider backend={HTML5Backend}>
          <Grid container spacing={2}>
            {images.map((_, index) => (
              <Grid key={index} item xs={3}>
                <DropZone index={index}>
                  <DraggableImage index={index} />
                </DropZone>
              </Grid>
            ))}
          </Grid>
        </DndProvider>
      </Box>

      {/* <Box mt={4}>
        <Button variant="contained" onClick={handleSave} disabled={!isChanged}>
          Save
        </Button>
      </Box> */}

      <StyledDialog open={openConfirmStyledDialog} onClose={cancelDelete}>
        <StyledDialogTitle>Confirm Deletion</StyledDialogTitle>
        <StyledDialogContent>
          <StyledDialogContentText>
            Are you sure you want to delete the image "
            {deleteIndex !== null && images[deleteIndex]?.name}"?
          </StyledDialogContentText>
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={handleDeleteImage} color="primary">
            Ok
          </Button>
        </StyledDialogActions>
      </StyledDialog>
    </Container>
  );
};

export default ImageEntryForm;
